import api from './authService'
import { crearErrorAplicacionApi } from './apiError'

export interface Categoria {
  id: string
  nombre: string
  descripcion?: string
  color: string
  icono: string
  nivel: number
  padre?: string
  subcategorias?: Categoria[]
  estado: 'activa' | 'inactiva'
  tipo: 'principal' | 'subcategoria'
  fechaCreacion: string
  fechaModificacion?: string
  totalArchivos: number
  orden: number
}

export interface FiltrosCategorias {
  busqueda?: string
  estado?: string
  tipo?: string
  nivel?: number
  padre?: string
}

export interface EstadisticasCategorias {
  total: number
  principales: number
  subcategorias: number
  activas: number
  totalArchivos: number
}

export const categoriasService = {
  /**
   * Obtener lista de categorías con filtros
   */
  async obtenerCategorias(filtros?: FiltrosCategorias): Promise<Categoria[]> {
    try {
      const params = new URLSearchParams()
      if (filtros?.busqueda) params.append('busqueda', filtros.busqueda)
      if (filtros?.estado) params.append('estado', filtros.estado)
      if (filtros?.tipo) params.append('tipo', filtros.tipo)
      if (filtros?.nivel !== undefined) params.append('nivel', String(filtros.nivel))
      if (filtros?.padre) params.append('padre', filtros.padre)

      const response = await api.get(`/api/categorias?${params.toString()}`)
      return response.data.map((categoria: any) => this.normalizarCategoria(categoria))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener categorías')
    }
  },

  /**
   * Obtener categorías en estructura de árbol
   */
  async obtenerArbolCategorias(): Promise<Categoria[]> {
    try {
      const response = await api.get('/api/categorias/arbol')
      return response.data.map((categoria: any) => this.normalizarCategoria(categoria))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener árbol de categorías')
    }
  },

  /**
   * Obtener una categoría por ID
   */
  async obtenerCategoria(id: string): Promise<Categoria> {
    try {
      const response = await api.get(`/api/categorias/${id}`)
      return this.normalizarCategoria(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener categoría')
    }
  },

  /**
   * Crear una nueva categoría
   */
  async crearCategoria(datos: Omit<Categoria, 'id' | 'fechaCreacion' | 'fechaModificacion' | 'totalArchivos'>): Promise<Categoria> {
    try {
      const response = await api.post('/api/categorias', {
        nombre: datos.nombre,
        descripcion: datos.descripcion,
        color: datos.color,
        icono: datos.icono,
        nivel: datos.nivel,
        padre: datos.padre,
        estado: datos.estado,
        tipo: datos.tipo,
        orden: datos.orden,
      })
      return this.normalizarCategoria(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al crear categoría')
    }
  },

  /**
   * Actualizar una categoría
   */
  async actualizarCategoria(id: string, datos: Partial<Categoria>): Promise<Categoria> {
    try {
      const response = await api.put(`/api/categorias/${id}`, {
        nombre: datos.nombre,
        descripcion: datos.descripcion,
        color: datos.color,
        icono: datos.icono,
        nivel: datos.nivel,
        padre: datos.padre,
        estado: datos.estado,
        tipo: datos.tipo,
        orden: datos.orden,
      })
      return this.normalizarCategoria(response.data)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al actualizar categoría')
    }
  },

  /**
   * Eliminar una categoría
   */
  async eliminarCategoria(id: string): Promise<void> {
    try {
      await api.delete(`/api/categorias/${id}`)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al eliminar categoría')
    }
  },

  /**
   * Obtener estadísticas de categorías
   */
  async obtenerEstadisticas(): Promise<EstadisticasCategorias> {
    try {
      const response = await api.get('/api/categorias/estadisticas')
      const stats = response.data
      return {
        total: stats.total || 0,
        principales: stats.principales || stats.main || 0,
        subcategorias: stats.subcategorias || stats.subcategories || 0,
        activas: stats.activas || stats.active || 0,
        totalArchivos: stats.total_archivos || stats.total_files || 0,
      }
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener estadísticas')
    }
  },

  /**
   * Reordenar categorías
   */
  async reordenarCategorias(categorias: { id: string; orden: number }[]): Promise<void> {
    try {
      await api.put('/api/categorias/reordenar', { categorias })
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al reordenar categorías')
    }
  },

  /**
   * Obtener categorías padre disponibles
   */
  async obtenerCategoriasPadre(): Promise<Categoria[]> {
    try {
      const response = await api.get('/api/categorias/padre')
      return response.data.map((categoria: any) => this.normalizarCategoria(categoria))
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener categorías padre')
    }
  },

  /**
   * Normalizar datos de categoría del backend al frontend
   */
  normalizarCategoria(categoria: any): Categoria {
    return {
      id: String(categoria.id),
      nombre: categoria.nombre,
      descripcion: categoria.descripcion,
      color: categoria.color || '#3B82F6',
      icono: categoria.icono || 'FolderIcon',
      nivel: categoria.nivel || 0,
      padre: categoria.padre || categoria.parent_id,
      subcategorias: categoria.subcategorias?.map((sub: any) => this.normalizarCategoria(sub)) || [],
      estado: categoria.estado || 'activa',
      tipo: categoria.tipo || (categoria.nivel === 0 ? 'principal' : 'subcategoria'),
      fechaCreacion: categoria.fecha_creacion || categoria.created_at || new Date().toISOString(),
      fechaModificacion: categoria.fecha_modificacion || categoria.updated_at,
      totalArchivos: categoria.total_archivos || categoria.file_count || 0,
      orden: categoria.orden || categoria.order || 0,
    }
  },
}
